import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import localeApi from '@services/locale';
import { RootState } from '@store/store';
import { cookies } from '@utils/cookies';
import { handleError } from '@utils/common';

interface AppState {
    locale: string;
    messages: Record<string, string>;
    loading: boolean;
}

const initialState: AppState = {
    locale: cookies.get('locale') || 'en',
    messages: {},
    loading: false,
};

const changeLocale = createAsyncThunk(
    'translation/changeLocale',
    async (localeInfo: { locale: string; localeName?: string }, thunkAPI) => {
        const { locale, localeName } = localeInfo;
        try {
            thunkAPI.dispatch(translationSlice.actions.setLoading(true));
            const messages = await thunkAPI
                .dispatch(localeApi.endpoints.getTranslations.initiate(locale, { forceRefetch: true }))
                .unwrap();
            cookies.set('locale', locale ?? 'en', { path: '/' });
            return { locale, messages };
        } catch (error) {
            // Set default locale to English if there's an error loading the messages
            cookies.set('locale', 'en', { path: '/' });
            handleError(`Error loading messages for locale "${localeName ?? locale}"`, error);
            console.error(`Error loading messages for locale "${localeName ?? locale}":`, error);
        } finally {
            thunkAPI.dispatch(translationSlice.actions.setLoading(false));
        }
    }
);

export const translationSlice = createSlice({
    name: 'translation',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(changeLocale.fulfilled, (state, action) => {
            state.loading = false;
            if (!action.payload) return;

            state.locale = action.payload.locale ?? 'en';
            state.messages = action.payload.messages ?? {};
        });
    },
});

const selectLocale = (state: RootState) => state.translation.locale;
const selectIsMessagesLoading = (state: RootState) => state.translation.loading;
const selectMessages = (state: RootState) => state.translation.messages;

export { changeLocale, selectIsMessagesLoading, selectLocale, selectMessages };

export default translationSlice.reducer;
